import {canSeeTestGames} from '../../shared/game-visibility';
import { lazy, Suspense } from 'react';
import { useTheme } from '../theme';
const IslandHome = lazy(() => import('./IslandHome').then((m) => ({ default: m.IslandHome })));
import { Leaderboard } from '../components/Leaderboard';
import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Play, Search, Gamepad2 } from 'lucide-react';
import { usePlatform } from '../store';
import { gameTitle, gameHero } from '../game-presentation';
import { GameCard } from '../components/GameCard';
import { CATALOG_CATEGORIES, matchesCategory } from '../catalog-categories';
export function Home() {
  const { pageStyle } = useTheme();
  return pageStyle === 'island' ? (
    <Suspense fallback={<div className="loading-panel">正在驶向小岛…</div>}>
      <IslandHome />
    </Suspense>
  ) : (
    <ClassicHome />
  );
}
function ClassicHome() {
  const { data, error, refresh } = usePlatform(),
    { pathname } = useLocation();
  const [query, setQuery] = useState(''),
    [category, setCategory] = useState('全部'),
    [sort, setSort] = useState('recommended');
  const [featureIndex, setFeatureIndex] = useState(0);
  const library = pathname !== '/';
  const games = useMemo(() => {
    let result = (data?.games || []).filter(
      (g) =>
        (pathname !== '/favorites' || data?.favorites.includes(g.id)) &&
        (pathname !== '/recent' || data?.recents.includes(g.id)) &&
        matchesCategory(g, category) &&
        [g.name, gameTitle(g), g.description, ...g.categories]
          .join(' ')
          .toLowerCase()
          .includes(query.toLowerCase()),
    );
    if (sort === 'rating') result = result.sort((a, b) => b.rating - a.rating);
    if (sort === 'new') result = result.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    if (pathname === '/recent')
      result = result.sort(
        (a, b) => (data?.recents.indexOf(a.id) || 0) - (data?.recents.indexOf(b.id) || 0),
      );
    return result;
  }, [data, pathname, query, category, sort]);
  if (error)
    return (
      <div className="empty-state">
        <h2>暂时无法连接</h2>
        <p>{error}</p>
        <button className="button" onClick={() => void refresh()}>
          重新加载
        </button>
      </div>
    );
  if (!data)
    return (
      <div className="loading-panel">
        <span className="spinner" />
        正在寻找好玩的游戏…
      </div>
    );
  const featured = [
    ...data.games.filter((g) => g.featured),
    ...data.games.filter((g) => !g.featured),
  ].slice(0, 3);
  const hero = featured[featureIndex % Math.max(1, featured.length)];
  return (
    <>
      {library && (
        <div className="page-heading">
          <div>
            <div className="eyebrow">MOYU ISLAND / A LITTLE ESCAPE</div>
            <h1>
              {pathname === '/favorites'
                ? '喜欢的，都在这里。'
                : pathname === '/recent'
                  ? '接着上一局的快乐。'
                  : library
                    ? '你的下一局，在这里。'
                    : data.settings.heroTitle}
            </h1>
            <p>
              {library ? `${games.length} 款游戏 · 随时开始，随时回来` : data.settings.heroSubtitle}
            </p>
          </div>
          <div className="today-note">
            <span className="tiny-orbit" />
            <span>今天，也来岛上歇一会儿</span>
          </div>
        </div>
      )}
      {!library && hero && (
        <div className="home-feature-row">
          <section className="coastal-feature" aria-label="推荐游戏">
            <img className="coastal-feature-art" src={gameHero(hero) || '/art/miner.webp'} alt="" />
            <div className="coastal-feature-shade" />
            <div className="coastal-feature-copy">
              <span className="eyebrow">MOYU ISLAND / A LITTLE ESCAPE</span>
              <h1>{gameTitle(hero)}</h1>
              <p>{hero.description}</p>
              <Link
                className="button primary"
                to={hero.free || data.user?.member ? '/play/' + hero.id : '/game/' + hero.slug}
              >
                <Play size={18} fill="currentColor" />
                {hero.free || data.user?.member ? '立即开玩' : '查看详情'}
              </Link>
            </div>
            {featured.length > 1 && (
              <div className="coastal-feature-dots" aria-label="切换推荐游戏">
                {featured.map((game, index) => (
                  <button
                    key={game.id}
                    aria-label={`推荐：${gameTitle(game)}`}
                    aria-pressed={hero.id === game.id}
                    onClick={() => setFeatureIndex(index)}
                  />
                ))}
              </div>
            )}
          </section>
          <Leaderboard />
        </div>
      )}
      <section className="catalog-section coastal-catalog">
        <div className="section-title">
          <h2>
            {pathname === '/favorites'
              ? '我的收藏'
              : pathname === '/recent'
                ? '最近游玩'
                : library
                  ? '游戏库'
                  : '岛上好玩，慢慢发现。'}
            {library && <span>{games.length.toString().padStart(2, '0')}</span>}
          </h2>
          {!library && <p className="coastal-catalog-note">把忙碌留在岸边，给自己放个小假。</p>}
          <div className="catalog-search">
            <Search size={17} />
            <input
              aria-label="搜索游戏"
              placeholder="搜索游戏"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select aria-label="游戏排序" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="recommended">推荐排序</option>
              <option value="rating">评分优先</option>
              <option value="new">最近更新</option>
            </select>
          </div>
          {!library && (
            <Link to="/library">
              查看全部
              <ArrowUpRight size={17} />
            </Link>
          )}
        </div>
        <div className="catalog-toolbar">
          <div className="filter-chips">
            {['全部', '免费', ...CATALOG_CATEGORIES.map((group) => group.name), ...(canSeeTestGames(data?.user) ? ['测试游戏'] : [])].map((c) => (
              <button
                className={c === category ? 'active' : ''}
                key={c}
                aria-pressed={c === category}
                onClick={() => setCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
        {games.length ? (
          <div className="game-grid">
            {games.map((g) => (
              <GameCard game={g} key={g.id} compact />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <Gamepad2 size={40} />
            <h3>{pathname === '/favorites' ? '还没有收藏的游戏' : '暂时没有符合条件的游戏'}</h3>
            <p>换个关键词，或者去游戏库逛逛。</p>
            <Link to="/library" className="button secondary">
              发现游戏
              <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </section>
      {!library && data.settings.announcementEnabled && data.settings.announcement && (
        <details className="coastal-announcement">
          <summary>站点公告</summary>
          <p>{data.settings.announcement}</p>
        </details>
      )}
    </>
  );
}
